/**
 * Reading an RFC 9457 `application/problem+json` error body — the estate's error
 * contract, which endpoints adopt as they are touched. One reader, so every module
 * that converges shows the same thing for the same body.
 */

/**
 * Bounds what a `detail` can put on screen. Longer ones are dropped, not truncated:
 * a detail this long is not the one-line, user-safe sentence the contract promises.
 * Same bound as `ApiService`'s 422 path.
 */
const MAX_DETAIL_CHARS = 400;

/** The showable parts of a problem+json body; each is `null` when absent or too long. */
export interface ProblemParts {
  title: string | null;
  detail: string | null;
  /** The problem `type` URI (the backend emits `<base>/<kind>`); `null` when absent. */
  type: string | null;
}

function showable(value: unknown): string | null {
  const text = typeof value === "string" ? value.trim() : "";
  return text && text.length <= MAX_DETAIL_CHARS ? text : null;
}

/**
 * The problem's `title` and `detail`, both `null` when the body is not
 * problem+json. The `title` is constant per problem type; the `detail` is the
 * per-occurrence sentence, which the backend sets only when safe to show.
 */
export async function readProblem(res: Response): Promise<ProblemParts> {
  if (!res.headers.get("content-type")?.includes("application/problem+json"))
    return { title: null, detail: null, type: null };
  try {
    const body = (await res.json()) as {
      title?: unknown;
      detail?: unknown;
      type?: unknown;
    };
    return {
      title: showable(body.title),
      detail: showable(body.detail),
      type: showable(body.type),
    };
  } catch {
    return { title: null, detail: null, type: null };
  }
}

/**
 * The problem's `detail`, or `null` when the body is not problem+json, carries no
 * `detail`, or carries one too long to show. The backend only sets `detail` when it
 * is safe for the person who made the request; everything else is `null`, and the
 * caller shows a generic line.
 */
export async function readProblemDetail(res: Response): Promise<string | null> {
  return (await readProblem(res)).detail;
}

/**
 * The per-field messages of a 422 problem+json body (`errors.<field>`), keeping
 * the first string short enough to show: `errors.<field>` is an array of messages
 * (the estate convention) or, leniently, one string. `undefined` when the body is not
 * problem+json or carries none. Reads a clone, so the caller's body stays intact.
 */
export async function readFieldErrors(
  res: Response,
): Promise<Record<string, string> | undefined> {
  if (!res.headers.get("content-type")?.includes("application/problem+json"))
    return undefined;
  try {
    const body = (await res.clone().json()) as { errors?: unknown };
    if (typeof body.errors !== "object" || body.errors === null)
      return undefined;
    const shown: Record<string, string> = {};
    for (const [field, message] of Object.entries(body.errors)) {
      const text = Array.isArray(message)
        ? (message.map(showable).find((m) => m !== null) ?? null)
        : showable(message);
      if (text) shown[field] = text;
    }
    return Object.keys(shown).length > 0 ? shown : undefined;
  } catch {
    return undefined;
  }
}
